import { uid, isDefined } from "@dhx/ts-common/core";
import { awaitRedraw, el, inject } from "@dhx/ts-common/dom";
import { IViewLike, View } from "@dhx/ts-common/view";

import { ICell, ICellConfig, ILayout, ILayoutConfig, LayoutEvents, ILayoutEventHandlersMap } from "./types";
import { getBlockRange, getMarginSize } from "./helpers";
import { IEventSystem, EventSystem } from "@dhx/ts-common/events";
import { ScrollView } from "@dhx/ts-common/ScrollView";
import { Grid } from "@dhx/ts-grid";

export class Cell extends View implements ICell {
	public id: string;
	public config: ICellConfig;
	public events: IEventSystem<LayoutEvents, ILayoutEventHandlersMap>;
	public scrollView: ScrollView;

	protected _handlers: { [key: string]: (...args: any) => any };
	protected _disabled: string[] = [];
	protected _parent: ILayout;
	protected _ui: IViewLike;
	protected _resizerHandlers: any;

	private _isLastFlexCell: boolean;
	private _afterWindowResized: () => void;

	constructor(parent: string | HTMLElement | ILayout, config: ICellConfig) {
		super(parent, config);

		const p = parent as ILayout;
		if (p && p.isVisible) {
			this._parent = p;
		}
		if (this._parent && this._parent.events) {
			this.events = this._parent.events;
		} else {
			this.events = new EventSystem(this);
		}
		this.config.full =
			this.config.full === undefined
				? Boolean(
						this.config.header ||
							this.config.collapsable ||
							this.config.headerHeight ||
							this.config.headerIcon ||
							this.config.headerImage
				  )
				: this.config.full;

		this._afterWindowResized = this._resizedWindow.bind(this);
		this._initHandlers();
		this.id = this.config.id || uid();

		if (this._isXDirection() && !config.width) config.$autoWidth = true;
		if (!this._isXDirection() && !config.height) config.$autoHeight = true;
	}

	paint() {
		if (this.isVisible()) {
			const view = this.getRootView();
			if (view) {
				view.redraw();
			} else {
				this._parent?.paint();
			}
		}
	}
	isVisible() {
		// top level node
		if (!this._parent) {
			if (this._container && this._container.tagName) {
				return true;
			}
			return Boolean(this.getRootNode());
		}
		// check active view in case of multiview
		const active = this._parent.config?.activeView;
		if (active && active !== this.id) {
			return false;
		}
		// check that all parents of the cell are visible as well
		return !this.config.hidden && (!this._parent || this._parent.isVisible());
	}
	hide() {
		if (!this.events.fire(LayoutEvents.beforeHide, [this.id])) {
			return;
		}
		this.config.hidden = true;
		this._resetCellsSize();
		if (this._parent && this._parent.paint) {
			this._parent.paint();
		}
		this.events.fire(LayoutEvents.afterHide, [this.id]);
	}
	show() {
		if (!this.events.fire(LayoutEvents.beforeShow, [this.id])) {
			return;
		}
		if (this._parent && this._parent.config && this._parent.config.activeView !== undefined) {
			this._parent.config.activeView = this.id;
		} else {
			this.config.hidden = false;
		}
		if (this._parent && !this._parent.isVisible()) {
			this._parent.show();
		}
		this.paint();
		// [dirty]
		if (this._ui instanceof Grid && this._ui.config.keyNavigation) {
			awaitRedraw().then(() => {
				(this._ui as Grid).setColumns((this._ui as Grid).config.columns);
				this.paint();
			});
		}
		this.events.fire(LayoutEvents.afterShow, [this.id]);
	}
	expand(): void {
		if (!this.events.fire(LayoutEvents.beforeExpand, [this.id])) {
			return;
		}
		this.config.collapsed = false;
		this._checkNextSize() || this._checkNextSize(this._getAnyFlexCell());
		this.events.fire(LayoutEvents.afterExpand, [this.id]);
		this.paint();
	}
	collapse(): void {
		if (!this.events.fire(LayoutEvents.beforeCollapse, [this.id])) {
			return;
		}
		this.config.collapsed = true;
		this._checkNextSize() || this._checkNextSize(this._getAnyFlexCell());
		this.events.fire(LayoutEvents.afterCollapse, [this.id]);
		this.paint();
	}
	toggle(): void {
		if (this.config.collapsed) {
			this.expand();
		} else {
			this.collapse();
		}
	}
	protected _checkNextSize(cell?: ICell) {
		const nextCell = cell ?? this._getNextCell();
		if (!nextCell) return false;
		if (this._isXDirection() && nextCell.config.$autoWidth && nextCell.config.width) {
			nextCell.config.width = undefined;
			return true;
		}
		if (!this._isXDirection() && nextCell.config.$autoHeight && nextCell.config.height) {
			nextCell.config.height = undefined;
			return true;
		}
		return !cell ? nextCell._checkNextSize() : false;
	}
	getParent() {
		return this._parent;
	}
	destructor() {
		this.events && this.events.clear();
		window.removeEventListener("resize", this._afterWindowResized);

		if (typeof (this.getWidget() as any)?.destructor === "function") {
			(this.getWidget() as any).destructor();
		}

		this.config = this.events = this.id = this._parent = this._handlers = this._uid = this._disabled = this._resizerHandlers = null;

		this.unmount();
	}
	getWidget() {
		return this._ui;
	}
	getCellView() {
		return this._parent && this._parent.getRefs(this._uid);
	}
	attach(component: any, config?: any): IViewLike {
		this.config.html = null;
		if (typeof component === "object") {
			this._ui = component;
		} else if (typeof component === "string") {
			this._ui = new (window as any).dhx[component](null, config);
		} else if (typeof component === "function") {
			if (component.prototype instanceof View) {
				this._ui = new component(null, config);
			} else {
				this._ui = {
					getRootView() {
						return component(config);
					},
				};
			}
		}
		this.paint();
		// [dirty]
		if (this._ui instanceof Grid && this._ui.config.keyNavigation) {
			awaitRedraw().then(() => {
				(this._ui as Grid).setColumns((this._ui as Grid).config.columns);
				this.paint();
			});
		}
		return this._ui;
	}
	attachHTML(html: string): void {
		this.config.html = html;
		this.paint();
	}
	toVDOM(nodes?: any[]) {
		if (this.config === null) {
			this.config = {};
		}
		if (this.config.hidden) {
			return;
		}

		const style = this._calculateStyle();
		const stylePadding = isDefined(this.config.padding)
			? !isNaN(Number(this.config.padding))
				? { padding: `${this.config.padding}px` }
				: { padding: this.config.padding }
			: "";
		const fullStyle = this.config.full || this.config.html ? style : { ...style, ...stylePadding };

		let kids;
		if (!this.config.html) {
			if (this._ui) {
				let view = this._ui.getRootView();
				if (view.render) {
					view = inject(view);
				}
				kids = [view];
			} else {
				kids = nodes || null;
			}
		}
		const resizer =
			this.config.resizable && !this._isLastCell() && this._getNextCell() && !this.config.collapsed
				? el(
						".dhx_layout-resizer." +
							(this._isXDirection() ? "dhx_layout-resizer--x" : "dhx_layout-resizer--y"),
						{
							...this._resizerHandlers,
							_ref: "resizer_" + this._uid,
							tabindex: 0,
						},
						[
							el("span.dhx_layout-resizer__icon", {
								class:
									"dxi " +
									(this._isXDirection() ? "dxi-dots-vertical" : "dxi-dots-horizontal"),
							}),
						]
				  )
				: null;

		const handlers = {};
		if (this.config.on) {
			for (const key in this.config.on) {
				handlers["on" + key] = this.config.on[key];
			}
		}

		let typeClass = "";
		const isParent = (this.config as any).cols || (this.config as any).rows;
		if (this.config.type && isParent) {
			switch (this.config.type) {
				case "line":
					typeClass = " dhx_layout-line";
					break;
				case "wide":
					typeClass = " dhx_layout-wide";
					break;
				case "space":
					typeClass = " dhx_layout-space";
					break;
				default:
					break;
			}
		}

		const cell = el(
			"div",
			{
				_key: this._uid,
				_ref: this._uid,
				["aria-label"]: this.config.id ? "tab-content-" + this.config.id : null,
				...handlers,
				class:
					this._getCss(false) +
					(this.config.css ? " " + this.config.css : "") +
					(this.config.collapsed ? " dhx_layout-cell--collapsed" : "") +
					(this.config.resizable ? " dhx_layout-cell--resizable" : "") +
					(this.config.type && !this.config.full ? typeClass : ""),
				style: fullStyle,
			},
			this.config.full
				? [
						el(
							"div",
							{
								tabindex: this.config.collapsable ? "0" : "-1",
								role: this.config.collapsable ? "button" : null,
								"aria-label": this.config.collapsable
									? `click to ${this.config.collapsed ? "expand" : "collapse"}`
									: null,
								class:
									"dhx_layout-cell-header" +
									(this._isXDirection()
										? " dhx_layout-cell-header--col"
										: " dhx_layout-cell-header--row") +
									(this.config.collapsable ? " dhx_layout-cell-header--collapseble" : "") +
									(this.config.collapsed ? " dhx_layout-cell-header--collapsed" : "") +
									(((this.getParent() || ({} as any)).config || ({} as any)).isAccordion
										? " dhx_layout-cell-header--accordion"
										: ""),
								style: {
									height: this.config.headerHeight,
								},
								onclick: this._handlers.toggle,
								onkeydown: this._handlers.enterCollapse,
							},
							[
								this.config.headerIcon &&
									el("span.dhx_layout-cell-header__icon", {
										class: this.config.headerIcon,
									}),
								this.config.headerImage &&
									el(".dhx_layout-cell-header__image-wrapper", [
										el("img", {
											src: this.config.headerImage,
											class: "dhx_layout-cell-header__image",
										}),
									]),
								this.config.header &&
									el("h3.dhx_layout-cell-header__title", this.config.header),
								this.config.collapsable
									? el("div.dhx_layout-cell-header__collapse-icon", {
											class: this._getCollapseIcon(),
									  })
									: el("div.dhx_layout-cell-header__collapse-icon", {
											class: "dxi dxi-empty",
									  }),
							]
						),
						!this.config.collapsed
							? el(
									"div",
									{
										style: {
											...stylePadding,
											height: `calc(100% - ${this.config.headerHeight || 37}px)`,
										},
										class:
											this._getCss(true) +
											" dhx_layout-cell-content" +
											(this.config.type ? typeClass : ""),
									},
									this.config.html
										? [
												el("div", {
													".innerHTML": this.config.html,
													class: "dhx_layout-cell dhx_layout-cell-inner_html",
												}),
										  ]
										: kids
							  )
							: null,
				  ]
				: this.config.html &&
				  !(
						(this.config as ILayoutConfig).rows &&
						(this.config as ILayoutConfig).cols &&
						(this.config as ILayoutConfig).views
				  )
				? [
						!this.config.collapsed
							? el(".dhx_layout-cell-content", { style: stylePadding }, [
									el(".dhx_layout-cell-inner_html", {
										".innerHTML": this.config.html,
									}),
							  ])
							: null,
				  ]
				: kids
		);

		return resizer ? [cell, resizer] : cell;
	}

	protected _getCss(_content?: boolean) {
		return "dhx_layout-cell";
	}
	protected _initHandlers() {
		window.addEventListener("resize", this._afterWindowResized);
		this._handlers = {
			enterCollapse: (e: KeyboardEvent) => {
				if (e.keyCode === 13) {
					this._handlers.toggle();
				}
			},
			collapse: () => {
				if (!this.config.collapsable) {
					return;
				}
				this.collapse();
			},
			expand: () => {
				if (!this.config.collapsable) {
					return;
				}
				this.expand();
			},
			toggle: () => {
				if (!this.config.collapsable) {
					return;
				}
				this.toggle();
			},
		};
		const blockOpts = {
			left: null,
			top: null,
			isActive: false,
			range: null,
			xLayout: null,
			nextCell: null,
			size: null,
			resizerLength: null,
			margin: null,
			collapsedSize: null,
		};

		const resizeMove = (event: MouseEvent & TouchEvent, startCoords = { x: 0, y: 0 }) => {
			if (!blockOpts.isActive) {
				return;
			}
			const xLayout = blockOpts.xLayout;
			const clientX = event.targetTouches
				? event.targetTouches[0].clientX
				: event.clientX + startCoords.x;
			const clientY = event.targetTouches
				? event.targetTouches[0].clientY
				: event.clientY + startCoords.y;
			let newValue = xLayout
				? clientX - blockOpts.range.min + window.pageXOffset
				: clientY - blockOpts.range.min + window.pageYOffset;
			const prop = xLayout ? "width" : "height";
			if (newValue < 0) {
				newValue = blockOpts.resizerLength / 2;
			} else if (newValue > blockOpts.size) {
				newValue = blockOpts.size - blockOpts.resizerLength;
			}

			const getValue = key => parseInt(this.config[key]?.toString());
			const maxSize = getValue(xLayout ? "maxWidth" : "maxHeight");
			const minSize = getValue(xLayout ? "minWidth" : "minHeight");
			if ((!maxSize || newValue < maxSize) && (!minSize || newValue > minSize)) {
				this.config[prop] = newValue - blockOpts.resizerLength / 2 + "px";
				blockOpts.nextCell.config[prop] =
					blockOpts.size - newValue - blockOpts.resizerLength / 2 + "px";
				if (blockOpts.nextCell._getAnyFlexCell())
					blockOpts.nextCell._getAnyFlexCell().config[prop] = undefined;
				this.paint();
				this.events.fire(LayoutEvents.resize, [this.id]);
			}
		};
		const iframesInfo = {};

		const resizeEnd = (event: TouchEvent & MouseEvent) => {
			blockOpts.isActive = false;
			document.body.classList.remove("dhx_no-select--resize");
			if (!event.targetTouches) {
				document.removeEventListener("mouseup", resizeEnd);
				document.removeEventListener("mousemove", resizeMove);
				for (const index in iframesInfo) {
					iframesInfo[index].node.contentWindow.document.removeEventListener("mouseup", resizeEnd);
					iframesInfo[index].node.contentWindow.document.removeEventListener(
						"mousemove",
						iframesInfo[index].listener
					);
				}
			} else {
				document.removeEventListener("touchend", resizeEnd);
				document.removeEventListener("touchmove", resizeMove);
				for (const index in iframesInfo) {
					iframesInfo[index].node.contentWindow.document.removeEventListener("touchend", resizeEnd);
					iframesInfo[index].node.contentWindow.document.removeEventListener(
						"touchmove",
						iframesInfo[index].listener
					);
				}
			}
			this.events.fire(LayoutEvents.afterResizeEnd, [this.id]);
		};

		const resizeStart = (event: MouseEvent & TouchEvent) => {
			event.targetTouches && event.preventDefault();

			if (event.which === 3) {
				return;
			}
			if (blockOpts.isActive) {
				resizeEnd(event);
			}

			if (!this.events.fire(LayoutEvents.beforeResizeStart, [this.id])) {
				return;
			}

			document.body.classList.add("dhx_no-select--resize");

			const block = this.getCellView();
			const nextCell = this._getNextCell();
			const nextBlock = nextCell.getCellView();
			const resizerBlock = this._getResizerView();
			const blockOffsets = block.el.getBoundingClientRect();
			const resizerOffsets = resizerBlock.el.getBoundingClientRect();
			const nextBlockOffsets = nextBlock.el.getBoundingClientRect();

			blockOpts.xLayout = this._isXDirection();

			blockOpts.left = blockOffsets.left + window.pageXOffset;
			blockOpts.top = blockOffsets.top + window.pageYOffset;

			blockOpts.collapsedSize = this._getCollapsedSize(this, nextCell);
			blockOpts.margin = getMarginSize(this.getParent().config, blockOpts.xLayout);
			blockOpts.range = getBlockRange(blockOffsets, nextBlockOffsets, blockOpts.xLayout);
			blockOpts.size =
				blockOpts.range.max - blockOpts.range.min - blockOpts.margin - blockOpts.collapsedSize;
			blockOpts.isActive = true;
			blockOpts.nextCell = nextCell;
			blockOpts.resizerLength = blockOpts.xLayout ? resizerOffsets.width : resizerOffsets.height;
		};

		this._resizerHandlers = {
			onmousedown: e => {
				resizeStart(e);
				document.addEventListener("mouseup", resizeEnd);
				document.addEventListener("mousemove", resizeMove);
				const iframes = document.querySelectorAll("iframe");
				if (iframes.length) {
					iframes.forEach((iframe, index) => {
						const iframeCoords = {
							x: iframe.getBoundingClientRect().x,
							y: iframe.getBoundingClientRect().y,
						};
						iframesInfo[index] = {
							node: iframe,
							listener: event => resizeMove(event, iframeCoords),
						};
					});
					for (const index in iframesInfo) {
						iframesInfo[index].node.contentWindow.document.addEventListener("mouseup", resizeEnd);
						iframesInfo[index].node.contentWindow.document.addEventListener(
							"mousemove",
							iframesInfo[index].listener
						);
					}
				}
			},
			ontouchstart: e => {
				resizeStart(e);
				document.addEventListener("touchend", resizeEnd);
				document.addEventListener("touchmove", resizeMove);
				const iframes = document.querySelectorAll("iframe");
				if (iframes.length) {
					iframes.forEach((iframe, index) => {
						const iframeCoords = {
							x: iframe.getBoundingClientRect().x,
							y: iframe.getBoundingClientRect().y,
						};
						iframesInfo[index] = {
							node: iframe,
							listener: event => resizeMove(event, iframeCoords),
						};
					});
					for (const index in iframesInfo) {
						iframesInfo[index].node.contentWindow.document.addEventListener(
							"touchend",
							resizeEnd
						);
						iframesInfo[index].node.contentWindow.document.addEventListener(
							"touchmove",
							iframesInfo[index].listener
						);
					}
				}
			},
			ondragstart: e => e.preventDefault(),
		};
	}
	protected _getCollapsedSize(cell: ICell, nextCell: ICell) {
		let collapsedSize = 0;
		const parent = this._parent as any;
		const index = parent._cells.indexOf(cell);
		const nextIndex = parent._cells.indexOf(nextCell);
		if (nextIndex - index === 1) return collapsedSize;
		for (let i = index + 1; i < nextIndex; i++) {
			if (parent._cells[i].config.collapsed) {
				if (!this._isXDirection()) {
					collapsedSize += Number(parent._cells[i].config.headerHeight) || 37;
				} else {
					collapsedSize += 45;
				}
			}
		}
		return collapsedSize;
	}
	protected _getCollapseIcon() {
		if (this._isXDirection() && this.config.collapsed) {
			return "dxi dxi-chevron-right";
		}
		if (this._isXDirection() && !this.config.collapsed) {
			return "dxi dxi-chevron-left";
		}
		if (!this._isXDirection() && this.config.collapsed) {
			return "dxi dxi-chevron-up";
		}
		if (!this._isXDirection() && !this.config.collapsed) {
			return "dxi dxi-chevron-down";
		}
	}
	protected _isLastCell() {
		const parent = this._parent as any;
		return parent && parent._cells.indexOf(this) === parent._cells.length - 1;
	}
	protected _getNextCell() {
		const parent = this._parent as any;
		const index = parent._cells.indexOf(this);
		const nextCell = !this._isLastCell() && parent._cells[index + 1];
		if (!nextCell) return false;
		if (nextCell.config.hidden || nextCell.config.collapsed) {
			return nextCell._getNextCell();
		} else return nextCell;
	}
	protected _getAnyFlexCell(selfInclude = false) {
		const parent = this._parent as any;
		const prop = this._isXDirection() ? "$autoWidth" : "$autoHeight";
		const cells = parent?._cells?.filter(
			cell => cell.config[prop] === true && (selfInclude ? true : cell.id !== this.id)
		);
		return cells?.length ? cells[cells.length - 1] : false;
	}
	protected _getResizerView() {
		return this._parent.getRefs("resizer_" + this._uid);
	}
	protected _isXDirection() {
		return this._parent && (this._parent as any)._xLayout;
	}
	protected _calculateStyle() {
		const config = this.config;
		if (!config) {
			return;
		}
		const style: any = {};
		let autoWidth = false;
		let autoHeight = false;

		if (!isNaN(Number(config.width))) config.width = config.width + "px";
		if (!isNaN(Number(config.height))) config.height = config.height + "px";
		if (!isNaN(Number(config.minWidth))) config.minWidth = config.minWidth + "px";
		if (!isNaN(Number(config.minHeight))) config.minHeight = config.minHeight + "px";
		if (!isNaN(Number(config.maxWidth))) config.maxWidth = config.maxWidth + "px";
		if (!isNaN(Number(config.maxHeight))) config.maxHeight = config.maxHeight + "px";

		if (config.width === "content") autoWidth = true;
		if (config.height === "content") autoHeight = true;
		// if (this._isXDirection() && !config.width) config.$autoWidth = true;
		// if (!this._isXDirection() && !config.height) config.$autoHeight = true;

		const {
			width,
			height,
			cols,
			rows,
			minWidth,
			minHeight,
			maxWidth,
			maxHeight,
			gravity,
			collapsed,
			$fixed,
		} = config as any;

		let gravityNumber = Math.sign(gravity) === -1 ? 0 : gravity;
		if (typeof gravity === "boolean") {
			gravityNumber = gravity ? 1 : 0;
		}
		let fixed = typeof gravity === "boolean" ? !gravity : Math.sign(gravity) === -1;
		if (this._isXDirection()) {
			if ($fixed || width || (gravity === undefined && (minWidth || maxWidth))) {
				fixed = true;
			}
		} else {
			if ($fixed || height || (gravity === undefined && (minHeight || maxHeight))) {
				fixed = true;
			}
		}
		const grow = fixed ? 0 : gravityNumber || 1;
		let fillSpace: string | boolean = this._isXDirection() ? "x" : "y";

		if (minWidth !== undefined && !collapsed) style.minWidth = minWidth;
		if (minHeight !== undefined && !collapsed) style.minHeight = minHeight;
		if (maxWidth !== undefined) style.maxWidth = maxWidth;
		if (maxHeight !== undefined) style.maxHeight = maxHeight;

		if (this._parent === undefined && !fillSpace !== undefined) {
			fillSpace = true;
		}

		if (width !== undefined && width !== "content") {
			style.width = width;
		} else {
			if (fillSpace === true) {
				style.width = "100%";
			} else if (fillSpace === "x") {
				if (autoWidth) {
					style.flex = "0 0 auto";
				} else {
					const isAuto = this._isXDirection() ? "1px" : "auto";
					style.flex = `${grow} ${cols || rows ? `0 ${isAuto}` : `1 auto`}`;
				}
			}
		}

		if (height !== undefined && height !== "content") {
			style.height = height;
		} else {
			if (fillSpace === true) {
				style.height = "100%";
			} else if (fillSpace === "y") {
				if (autoHeight) {
					style.flex = "0 0 auto";
				} else {
					const isAuto = !this._isXDirection() ? "1px" : "auto";
					style.flex = `${grow} ${cols || rows ? `0 ${isAuto}` : `1 auto`}`;
				}
			}
		}

		if (fillSpace === true && config.width === undefined && config.height === undefined) {
			style.flex = `${grow} 1 auto`;
		}

		if (collapsed) {
			if (this._isXDirection()) {
				style.width = "auto";
			} else {
				style.height = "auto";
			}
			style.flex = `0 0 auto`;
		}

		return style;
	}

	private _resizedWindow() {
		if (this._isLastFlexCell === undefined) {
			this._isLastFlexCell = this._getAnyFlexCell(true)?.id === this.id;
		} else if (!this._isLastFlexCell) {
			window.removeEventListener("resize", this._afterWindowResized);
		}
		if (this._isLastFlexCell) {
			const prop = this._isXDirection() ? "width" : "height";
			this.config[prop] = undefined;
			this.paint();
		}
	}
	private _resetCellsSize() {
		const cells = (this._parent as any)._cells.filter(cell => {
			return cell !== this && !cell.config.hidden && !cell.config.collapsed;
		});
		const direction = this._isXDirection();
		const autoSize = direction ? "$autoWidth" : "$autoHeight";
		const size = direction ? "width" : "height";
		cells.forEach(cell => {
			if (cell.config[autoSize]) {
				cell.config[size] = undefined;
			}
		});
	}
}
